import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PainterSimonsTown() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const painter = {
    id: "painters-simons-town",
    name: "Painters Simon's Town",
    location: "Simon's Town, Cape Town",
    rating: 4.9,
    reviews: 148,
    specialties: ["Heritage Properties", "Naval Housing", "Coastal Painting", "Historic Restoration"],
    image: "https://public.readdy.ai/ai/img_res/a27c0716982feffe76b387235ee433f6.jpg",
    verified: true,
    yearsExperience: 16,
    website: "https://painterssimonstown.co.za",
    email: "info@painterssimonstown.co.za",
    phone: "+27 21 555 0131",
    description: "Professional painting services in Simon's Town and surrounding naval areas. We specialize in heritage properties and historic restoration with expertise in marine-grade finishes for coastal conditions.",
    services: [
      "Interior House Painting",
      "Exterior House Painting", 
      "Heritage Property Restoration",
      "Naval Housing Painting",
      "Historic Building Painting",
      "Waterproofing Services",
      "Damp Proofing Solutions",
      "Color Consultation & Design"
    ],
    serviceAreas: ["Simon's Town", "Fish Hoek", "Glencairn", "Boulders Beach", "Miller's Point", "False Bay", "Naval Base"],
    gallery: [
      "https://readdy.ai/api/search-image?query=Beautiful%20interior%20living%20room%20painted%20by%20professional%20painters%20in%20Simons%20Town%20Cape%20Town%2C%20historic%20Victorian%20home%20with%20fresh%20painted%20walls%2C%20quality%20paint%20finish%2C%20False%20Bay%20ocean%20views%2C%20bright%20natural%20lighting%2C%20clean%20professional%20painting%20work%20with%20period%20features%20and%20elegant%20furniture&width=400&height=300&seq=simonstown-gallery-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Exterior%20heritage%20house%20painting%20project%20in%20Simons%20Town%20Cape%20Town%20South%20Africa%2C%20historic%20Victorian%20home%20with%20fresh%20paint%2C%20professional%20painters%20working%20on%20scaffolding%2C%20quality%20exterior%20finish%2C%20naval%20base%20area%20with%20ocean%20views%2C%20well-maintained%20historic%20property&width=400&height=300&seq=simonstown-gallery-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Naval%20housing%20painting%20project%20in%20Simons%20Town%20False%20Bay%2C%20professional%20painters%20working%20on%20military%20residential%20property%20exterior%2C%20quality%20paint%20finish%2C%20modern%20coastal%20architecture%20near%20naval%20base%2C%20organized%20work%20site%20with%20painting%20equipment&width=400&height=300&seq=simonstown-gallery-3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Historic%20building%20interior%20painting%20in%20Simons%20Town%20Cape%20Town%2C%20fresh%20painted%20walls%20in%20heritage%20property%2C%20quality%20paint%20finish%20with%20period%20colors%2C%20bright%20clean%20space%20with%20Victorian%20features%2C%20professional%20painting%20work%20with%20restored%20architectural%20details&width=400&height=300&seq=simonstown-gallery-4&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Bedroom%20interior%20painting%20in%20Simons%20Town%20heritage%20home%2C%20fresh%20painted%20walls%20in%20master%20bedroom%2C%20historic%20coastal%20design%20with%20ocean%20views%2C%20quality%20paint%20finish%2C%20bright%20airy%20space%2C%20professional%20painting%20work%20with%20period%20decor%20and%20natural%20lighting&width=400&height=300&seq=simonstown-gallery-5&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Exterior%20coastal%20heritage%20home%20painting%20in%20Simons%20Town%20Cape%20Town%2C%20professional%20painters%20applying%20marine-grade%20paint%20to%20historic%20property%2C%20False%20Bay%20ocean%20backdrop%2C%20quality%20exterior%20finish%20with%20weather%20protection%2C%20organized%20professional%20work%20site&width=400&height=300&seq=simonstown-gallery-6&orientation=landscape"
    ],
    testimonials: [
      {
        name: "Commander James Roberts",
        rating: 5,
        comment: "Outstanding work on our naval housing property. The team was professional, punctual, and delivered exceptional quality. They understand the unique requirements of coastal military housing."
      },
      {
        name: "Elizabeth Fourie", 
        rating: 5,
        comment: "Excellent restoration work on our Victorian heritage home. They respected the historic character while providing modern protection. Highly recommend for heritage properties in Simon's Town."
      },
      {
        name: "Peter Daniels",
        rating: 5,
        comment: "Professional painters who truly understand coastal and heritage painting. Our home looks beautiful and is well-protected against the harsh marine environment."
      }
    ]
  };

  useEffect(() => {
    document.title = "Painters Simon's Town | Professional Heritage Painting Services Cape Town | Naval Housing Painters";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Painters Simon's Town offers professional painting services for heritage properties and naval housing in Simon's Town, Cape Town. Specializing in historic restoration, marine-grade finishes, and coastal properties.");
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Painters Simons Town, house painters Simons Town, heritage painting contractors Cape Town, naval housing painting, historic restoration painting');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-32 min-h-[70vh] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20stunning%20historic%20Victorian%20home%20in%20Simons%20Town%20Cape%20Town%20South%20Africa%2C%20spectacular%20False%20Bay%20ocean%20views%20with%20naval%20base%2C%20painters%20in%20clean%20uniforms%20applying%20paint%20with%20brushes%20and%20rollers%2C%20heritage%20architecture%2C%20bright%20sunny%20day%2C%20pristine%20work%20environment%20with%20professional%20equipment&width=1200&height=600&seq=simonstown-painters-hero-bg&orientation=landscape)` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-block bg-lime-400 text-black px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-shield-check-line mr-2"></i>
              VERIFIED PROFESSIONAL PAINTER
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Painters Simon's Town
            </h1>
            <p className="text-2xl text-gray-300 mb-4">
              Heritage Property & Naval Housing Painting Specialists
            </p>
            <p className="text-lg text-gray-400 mb-8 leading-relaxed">
              Simon's Town's premier painting contractors specializing in heritage properties, naval housing, and coastal residences. With over 18 years of experience serving this historic naval town, we combine traditional craftsmanship with modern techniques to preserve and enhance properties of all types and ages.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content Area */}
            <div className="lg:col-span-2">
              {/* Navigation Tabs */}
              <nav className="flex space-x-8">
                {[
                  { id: 'overview', label: 'Overview' },
                  { id: 'services', label: 'Services' },
                  { id: 'gallery', label: 'Gallery' },
                  { id: 'reviews', label: 'Reviews' },
                  { id: 'contact', label: 'Contact' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                      activeTab === tab.id
                        ? 'border-lime-500 text-lime-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>

              {/* Tab Content */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                {activeTab === 'overview' && (
                  <div className="space-y-8">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">About Painters Simon's Town</h2>
                      <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
                        <p className="leading-relaxed">
                          Welcome to Painters Simon's Town, the historic naval town's most trusted and experienced painting contractor. For over 18 years, we have been the preferred choice for homeowners, property managers, and institutions throughout Simon's Town, Glencairn, Boulders, Red Hill, and the surrounding Southern Peninsula who value quality craftsmanship, attention to detail, and professional service excellence.
                        </p>
                        <p className="leading-relaxed">
                          Our reputation has been carefully built over nearly two decades through consistent delivery of exceptional quality workmanship, honest and transparent business practices, and an unwavering commitment to customer satisfaction. We understand that Simon's Town is a unique and special place, rich in history and character, with a diverse range of properties from Victorian-era heritage homes to modern naval housing and contemporary coastal residences. Each property type presents its own specific challenges and requirements, and we have developed specialized expertise in working with all of them.
                        </p>
                        <p className="leading-relaxed">
                          What truly sets Painters Simon's Town apart from other painting contractors is our deep understanding and appreciation of the town's unique character and the specific environmental challenges that affect properties in this area. The combination of salt-laden ocean breezes from False Bay, high humidity levels, strong south-easterly winds, occasional mountain mists, and intense UV radiation requires specialized knowledge, carefully selected materials, and proven application techniques. Our team has spent years developing and refining our methods to ensure that every project we complete provides both beautiful aesthetics and maximum protection against these challenging conditions.
                        </p>
                        <p className="leading-relaxed">
                          We take particular pride in our work on heritage properties throughout Simon's Town. These historic buildings require special care, sensitivity, and expertise to preserve their character while ensuring they remain protected and functional for future generations. Our team has extensive experience working with heritage structures, understanding the importance of using appropriate materials and techniques that respect the building's original construction while providing modern levels of protection and durability. We work closely with heritage consultants and local authorities when required to ensure all work meets conservation standards and regulations.
                        </p>
                        <p className="leading-relaxed">
                          Our comprehensive approach to every painting project begins with a thorough initial consultation and property assessment. We take the time to understand your specific needs, preferences, and concerns, carefully examining the condition of all surfaces to be painted, identifying any underlying issues that need to be addressed, and considering factors such as exposure to elements, substrate type, and previous coating history. Based on this detailed assessment, we provide expert recommendations and a transparent, itemized quote with no hidden costs or surprises.
                        </p>
                        <p className="leading-relaxed">
                          Surface preparation is where quality painting truly begins, and it's an area where we never compromise. We understand that even the finest paint will fail prematurely if applied to poorly prepared surfaces. Our preparation process is thorough and meticulous, including cleaning, scraping, sanding, filling, priming, and any necessary repairs. We address issues such as peeling paint, cracks, holes, water damage, and substrate deterioration before any new coatings are applied. This attention to detail in the preparation phase is what ensures the longevity and beauty of the final result.
                        </p>
                        <p className="leading-relaxed">
                          Our team of skilled painters are experienced craftsmen who take genuine pride in their work. Each team member has been carefully selected not only for their technical skills but also for their reliability, professionalism, and commitment to excellence. We invest in ongoing training to ensure our team stays current with the latest products, techniques, and industry best practices. This dedication to continuous improvement means you benefit from the most advanced and effective painting solutions available today.
                        </p>
                        <p className="leading-relaxed">
                          Over our 18 years of operation, we have built an impressive portfolio of completed projects throughout Simon's Town and the surrounding areas. From charming Victorian cottages and historic naval buildings to modern family homes and luxury coastal properties, we have successfully completed thousands of projects of all sizes and complexity levels. Each project receives the same level of dedication, professionalism, and attention to detail, regardless of its scope or budget. We treat every home as if it were our own, ensuring that the final result not only meets but exceeds your expectations.
                        </p>
                        <p className="leading-relaxed">
                          At Painters Simon's Town, we believe in building long-term relationships with our clients based on trust, transparency, and consistently excellent results. Many of our customers have been with us for years, returning for maintenance work, renovations, and new projects. They recommend us to their friends, family, neighbors, and colleagues because they know they can rely on us to deliver superior quality, honest advice, fair pricing, and professional service every single time. This loyalty and trust is something we deeply value and never take for granted.
                        </p>
                        <p className="leading-relaxed">
                          We use only premium, environmentally responsible paints and materials from leading manufacturers including Plascon, Dulux, and specialized marine coating suppliers. These products are specifically formulated to provide superior adhesion, durability, color retention, and resistance to the harsh coastal conditions found in Simon's Town. By investing in the best materials available and applying them with expert technique, we ensure that your paint job not only looks stunning on completion day but continues to look great and provide protection for many years to come, maximizing the value of your investment.
                        </p>
                      </div>
                    </div>

                    <div className="border-t pt-8">
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Choose Painters Simon's Town?</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-lime-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-award-line text-lime-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2">18+ Years Experience</h4>
                            <p className="text-gray-600 text-sm">Nearly two decades of proven excellence in heritage property and coastal painting throughout Simon's Town.</p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-lime-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-building-2-line text-lime-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2">Heritage Specialists</h4>
                            <p className="text-gray-600 text-sm">Extensive experience working with historic buildings, using appropriate materials and conservation techniques.</p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-lime-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-shield-check-line text-lime-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2">Fully Insured & Certified</h4>
                            <p className="text-gray-600 text-sm">Comprehensive liability insurance and professional certifications for your complete peace of mind.</p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-lime-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-customer-service-line text-lime-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2">Exceptional Service</h4>
                            <p className="text-gray-600 text-sm">Professional communication, respect for your property, and commitment to exceeding expectations.</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-8">
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Service Areas</h3>
                      <p className="text-gray-700 mb-4 leading-relaxed">
                        Painters Simon's Town proudly serves the entire Southern Peninsula region, including:
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {["Simon's Town", 'Glencairn', 'Boulders', 'Red Hill', 'Fish Hoek', 'Scarborough', 'Misty Cliffs', 'Miller\'s Point', 'Smitswinkel Bay'].map((area) => (
                          <div key={area} className="flex items-center space-x-2 text-gray-700">
                            <i className="ri-map-pin-line text-lime-600"></i>
                            <span>{area}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'services' && (
                  <div className="space-y-8">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Comprehensive Painting Services</h2>
                      <p className="text-gray-700 mb-8 leading-relaxed">
                        At Painters Simon's Town, we offer a complete range of professional painting services tailored to the unique needs of heritage properties, naval housing, and coastal residences. Our expertise covers every aspect of residential and commercial painting, from initial consultation through to final inspection and beyond.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                        <div className="w-14 h-14 bg-lime-400 rounded-lg flex items-center justify-center mb-4">
                          <i className="ri-building-2-line text-black text-2xl"></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">Heritage Property Restoration</h3>
                        <p className="text-gray-700 mb-4 leading-relaxed">
                          Specialized painting and restoration services for historic buildings throughout Simon's Town. We use traditional techniques combined with modern protective coatings to preserve the character and integrity of heritage structures while ensuring long-lasting protection and beauty.
                        </p>
                        <ul className="space-y-2 text-gray-600 text-sm">
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Victorian and Edwardian era properties</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Historic naval buildings and structures</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Period-appropriate color schemes and finishes</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Conservation-approved materials and techniques</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                        <div className="w-14 h-14 bg-lime-400 rounded-lg flex items-center justify-center mb-4">
                          <i className="ri-home-4-line text-black text-2xl"></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">Residential Interior Painting</h3>
                        <p className="text-gray-700 mb-4 leading-relaxed">
                          Transform your living spaces with our premium interior painting services. We specialize in creating beautiful, durable finishes that enhance your home's aesthetic appeal while providing long-lasting protection. Our meticulous preparation and expert application ensure flawless results.
                        </p>
                        <ul className="space-y-2 text-gray-600 text-sm">
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Living areas, bedrooms, and entertainment spaces</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Kitchens and bathrooms with moisture-resistant coatings</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Ceilings, cornices, and architectural details</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Doors, windows, and trim work</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                        <div className="w-14 h-14 bg-lime-400 rounded-lg flex items-center justify-center mb-4">
                          <i className="ri-building-line text-black text-2xl"></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">Coastal Exterior Painting</h3>
                        <p className="text-gray-700 mb-4 leading-relaxed">
                          Protect and beautify your property's exterior with our specialized coastal painting services. We use premium marine-grade paints and proven application techniques designed to withstand salt spray, UV radiation, wind, and moisture from False Bay's challenging coastal environment.
                        </p>
                        <ul className="space-y-2 text-gray-600 text-sm">
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Exterior walls with marine-grade protective coatings</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Windows, doors, and exterior trim</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Decks, patios, and outdoor living areas</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Fascias, soffits, and gutter systems</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                        <div className="w-14 h-14 bg-lime-400 rounded-lg flex items-center justify-center mb-4">
                          <i className="ri-shield-line text-black text-2xl"></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">Waterproofing & Protection</h3>
                        <p className="text-gray-700 mb-4 leading-relaxed">
                          Essential for coastal properties, our comprehensive waterproofing services protect your investment from water damage, mold growth, and structural deterioration. We use advanced waterproofing systems specifically designed for Simon's Town's unique coastal environment.
                        </p>
                        <ul className="space-y-2 text-gray-600 text-sm">
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Exterior wall waterproofing and sealing</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Roof waterproofing and coating systems</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Balcony and deck waterproofing</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Damp proofing and moisture barrier installation</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'gallery' && (
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-8">Project Gallery</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {painter.gallery.map((image, index) => (
                        <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                          <img 
                            src={image} 
                            alt={`${painter.name} project ${index + 1}`}
                            className="w-full h-64 object-cover object-top"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'reviews' && (
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-8">Customer Reviews</h2>
                    <div className="space-y-6">
                      {painter.testimonials.map((testimonial, index) => (
                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                          <div className="flex items-center mb-4">
                            <div className="flex items-center">
                              {[...Array(5)].map((_, i) => (
                                <i key={i} className={`ri-star-fill ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                              ))}
                            </div>
                            <span className="ml-3 font-semibold text-gray-900">{testimonial.name}</span>
                          </div>
                          <p className="text-gray-700">{testimonial.comment}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'contact' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Information</h2>
                      <div className="space-y-6">
                        <div className="flex items-center">
                          <i className="ri-mail-line text-2xl text-lime-500 mr-4"></i>
                          <div>
                            <h3 className="font-semibold text-gray-900">Email</h3>
                            <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                              {painter.email}
                            </a>
                          </div>
                        </div>
                        
                        <div className="flex items-center">
                          <i className="ri-global-line text-2xl text-lime-500 mr-4"></i>
                          <div>
                            <h3 className="font-semibold text-gray-900">Website</h3>
                            <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                              {painter.website}
                            </a>
                          </div>
                        </div>
                        
                        <div className="flex items-start">
                          <i className="ri-map-pin-line text-2xl text-lime-500 mr-4 mt-1"></i>
                          <div>
                            <h3 className="font-semibold text-gray-900">Service Area</h3>
                            <p className="text-gray-700">{painter.location} and surrounding areas</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">Get a Quote</h3>
                      <button 
                        onClick={() => navigate('/quote')}
                        className="w-full bg-lime-400 text-black py-4 rounded-lg font-semibold hover:bg-lime-500 transition-colors text-lg whitespace-nowrap cursor-pointer"
                      >
                        Request Free Quote
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Sidebar */}
            <div>
              <div className="bg-gray-50 p-6 rounded-lg sticky top-6">
                <div className="mb-6">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20heritage%20home%20in%20Simons%20Town%20Cape%20Town%2C%20False%20Bay%20naval%20area%20property%20with%20ocean%20views%2C%20painters%20in%20uniform%20with%20quality%20equipment%2C%20Victorian%20architecture%2C%20bright%20natural%20lighting&width=400&height=300&seq=simonstown-sidebar-image&orientation=landscape"
                    alt="Professional painters Simons Town heritage house painting services Cape Town False Bay"
                    className="w-full h-48 object-cover object-top rounded-lg"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Contact</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <i className="ri-mail-line mr-3 text-lime-500"></i>
                    <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                      {painter.email}
                    </a>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-global-line mr-3 text-lime-500"></i>
                    <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                      Visit Website
                    </a>
                  </div>
                </div>
                
                <button 
                  onClick={() => navigate('/quote')}
                  className="w-full mt-6 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Get Free Quote
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
